import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { fetchAdminSettings, adminUpdateSettings, fetchContent, fetchCalendar } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Sparkles, Save, Loader2, Pin, RefreshCw, CalendarDays, ListOrdered, Plus, X } from "lucide-react";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const todayISO = () => new Date().toISOString().slice(0, 10);
const dayIndex = () => Math.floor(Date.now() / 86400000);

export default function AdminSpotlight() {
  const { reload } = useSettings();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [filter, setFilter] = useState("");
  const [mode, setMode] = useState("auto"); // "auto" | "pinned" | "scheduled"
  const [pins, setPins] = useState([]); // one or more pinned article slugs (rotate daily if >1)
  const [schedule, setSchedule] = useState([]); // ordered slugs, one per day (weekly rotation)
  const [fmap, setFmap] = useState([]); // [{ date, label, slug }] festival / date pins
  const [copy, setCopy] = useState({});
  const [headings, setHeadings] = useState({});

  useEffect(() => {
    fetchAdminSettings().then((s) => {
      const sp = s.home_spotlight || {};
      const ph = s.page_headings || {};
      const legacyPin = (ph["home.spotlight"] || {}).pinned_slug || "";
      const loadedPins = Array.isArray(sp.slugs) && sp.slugs.length ? sp.slugs.filter(Boolean) : (sp.slug || legacyPin ? [sp.slug || legacyPin] : []);
      setPins(loadedPins);
      setSchedule(Array.isArray(sp.schedule) ? sp.schedule.filter(Boolean) : []);
      setFmap(Array.isArray(sp.festival_map) ? sp.festival_map.filter((f) => f && f.date) : []);
      setMode(sp.mode || (loadedPins.length ? "pinned" : "auto"));
      setCopy(ph["home.spotlight"] || {});
      setHeadings(ph);
      setLoaded(true);
    }).catch(() => setLoaded(true));
    fetchContent({ limit: 300 }).then(setContent).catch(() => {});
    fetchCalendar().then((d) => setFestivals(d.festivals || [])).catch(() => {});
  }, []);

  const setCopyField = (f, v) => setCopy((c) => ({ ...c, [f]: v }));
  // Pinned article helpers (supports multiple — they rotate daily when >1)
  const addPin = () => setPins((p) => [...p, ""]);
  const setPin = (i, v) => setPins((p) => p.map((x, j) => (j === i ? v : x)));
  const removePin = (i) => setPins((p) => p.filter((_, j) => j !== i));
  // Weekly rotation helpers
  const addDay = () => setSchedule((s) => (s.length >= 7 ? s : [...s, ""]));
  const setDay = (i, v) => setSchedule((s) => s.map((x, j) => (j === i ? v : x)));
  const removeDay = (i) => setSchedule((s) => s.filter((_, j) => j !== i));
  // Festival / date pin helpers
  const addFest = () => setFmap((m) => [...m, { date: "", label: "", slug: "" }]);
  const setFest = (i, patch) => setFmap((m) => m.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const removeFest = (i) => setFmap((m) => m.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const effPins = mode === "pinned" ? pins.filter(Boolean) : [];
      const effSlug = effPins[0] || "";
      const effSchedule = mode === "scheduled" ? schedule.filter(Boolean) : [];
      const effFmap = mode === "scheduled" ? fmap.filter((f) => f.date && f.slug) : [];
      const nextCopy = { ...copy };
      if (effSlug) nextCopy.pinned_slug = effSlug; else delete nextCopy.pinned_slug; // back-compat mirror
      const cleanCopy = {};
      Object.entries(nextCopy).forEach(([k, v]) => { if (typeof v === "string" && v.trim()) cleanCopy[k] = v.trim(); });
      const nextHeadings = { ...headings, "home.spotlight": cleanCopy };
      if (!Object.keys(cleanCopy).length) delete nextHeadings["home.spotlight"];
      await adminUpdateSettings({
        home_spotlight: { mode, slug: effSlug, slugs: effPins, schedule: effSchedule, festival_map: effFmap },
        page_headings: nextHeadings,
      });
      await reload();
      const desc = mode === "pinned" ? (effPins.length > 1 ? `${effPins.length} pinned articles rotate daily.` : "Pinned article set.") : mode === "scheduled" ? `${effFmap.length} festival pin(s), ${effSchedule.length}-day rotation.` : "Auto-rotating daily.";
      toast.success("Spotlight saved", { description: desc });
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const bySlug = useMemo(() => Object.fromEntries(content.map((c) => [c.slug, c])), [content]);
  const q = filter.trim().toLowerCase();
  const options = content.filter((c) => !q || `${c.title || ""} ${c.slug} ${c.type || ""}`.toLowerCase().includes(q)).slice(0, 200);
  const label = (s) => (bySlug[s] ? `${bySlug[s].title || s} · ${bySlug[s].type}` : s);

  // What shows on the homepage today, given the current (unsaved) config.
  const todaysSlug = useMemo(() => {
    if (mode === "pinned") { const p = pins.filter(Boolean); return p.length ? p[dayIndex() % p.length] : ""; }
    if (mode === "scheduled") {
      const t = todayISO();
      const fest = fmap.find((f) => f.date === t && f.slug);
      if (fest) return fest.slug;
      const qArr = schedule.filter(Boolean);
      if (qArr.length) return qArr[dayIndex() % qArr.length];
    }
    return "";
  }, [mode, pins, schedule, fmap]);

  const Picker = ({ value, onChange, testid }) => (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inp} data-testid={testid}>
      <option value="">— Select an article —</option>
      {value && !content.some((c) => c.slug === value) && <option value={value}>{value} (current)</option>}
      {options.map((c) => <option key={c.slug} value={c.slug}>{(c.title || c.slug)} · {c.type}</option>)}
    </select>
  );

  if (!loaded) return <div className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-spotlight">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 font-serif-latin text-3xl text-ink"><Sparkles className="h-6 w-6 text-saffron" /> Daily Spotlight</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-3">Control the homepage “In focus today” section. Pin one article, auto-rotate daily, or build a schedule — a weekly queue that auto-advances plus festival-day pins tied to the calendar.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="spotlight-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-6">
          {/* Mode */}
          <div className="border border-line bg-surface p-4">
            <p className="mb-3 font-serif-latin text-lg text-ink">What to show</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <button onClick={() => setMode("auto")} data-testid="spotlight-mode-auto" className={`flex items-center gap-2 border px-3 py-3 text-left text-sm transition-colors ${mode === "auto" ? "border-saffron bg-[var(--saffron)]/8 text-ink" : "border-line text-ink-2 hover:border-ink"}`}>
                <RefreshCw className={`h-4 w-4 shrink-0 ${mode === "auto" ? "text-saffron" : "text-ink-3"}`} /> Auto-rotate daily
              </button>
              <button onClick={() => setMode("pinned")} data-testid="spotlight-mode-pinned" className={`flex items-center gap-2 border px-3 py-3 text-left text-sm transition-colors ${mode === "pinned" ? "border-saffron bg-[var(--saffron)]/8 text-ink" : "border-line text-ink-2 hover:border-ink"}`}>
                <Pin className={`h-4 w-4 shrink-0 ${mode === "pinned" ? "text-saffron" : "text-ink-3"}`} /> Pin one article
              </button>
              <button onClick={() => setMode("scheduled")} data-testid="spotlight-mode-scheduled" className={`flex items-center gap-2 border px-3 py-3 text-left text-sm transition-colors ${mode === "scheduled" ? "border-saffron bg-[var(--saffron)]/8 text-ink" : "border-line text-ink-2 hover:border-ink"}`}>
                <CalendarDays className={`h-4 w-4 shrink-0 ${mode === "scheduled" ? "text-saffron" : "text-ink-3"}`} /> Schedule
              </button>
            </div>

            {(mode === "pinned" || mode === "scheduled") && (
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search articles by title / slug…" className={`${inp} mt-4`} data-testid="spotlight-search" />
            )}

            {mode === "pinned" && (
              <div className="mt-2 space-y-2" data-testid="spotlight-pins">
                {pins.map((s, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`spotlight-pin-row-${i}`}>
                    <div className="flex-1"><Picker value={s} onChange={(v) => setPin(i, v)} testid={`spotlight-pin-article-${i}`} /></div>
                    <button onClick={() => removePin(i)} data-testid={`spotlight-pin-remove-${i}`} className="inline-flex h-9 w-9 shrink-0 items-center justify-center border border-line text-ink-3 hover:border-[var(--saffron-dark)] hover:text-[var(--saffron-dark)]"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                {pins.length === 0 && <p className="text-xs text-ink-3">No articles pinned yet.</p>}
                <button onClick={addPin} data-testid="spotlight-add-pin" className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron"><Plus className="h-4 w-4" /> Add article</button>
                {pins.filter(Boolean).length > 1 && <p className="text-xs text-ink-3">These {pins.filter(Boolean).length} articles rotate automatically — one per day.</p>}
              </div>
            )}
          </div>

          {mode === "scheduled" && (
            <>
              {/* Festival / date pins */}
              <div className="border border-line bg-surface p-4" data-testid="spotlight-festival-pins">
                <div className="mb-1 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-saffron" /><p className="font-serif-latin text-lg text-ink">Festival & date pins</p></div>
                <p className="mb-3 text-xs text-ink-3">On these dates the chosen article takes over the spotlight (highest priority). Pick a festival to fill the date automatically, or set any date.</p>
                <div className="space-y-3">
                  {fmap.map((f, i) => (
                    <div key={i} className="rounded-sm border border-line/70 bg-parchment/40 p-3" data-testid={`spotlight-fest-row-${i}`}>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="block">
                          <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Festival</span>
                          <select
                            data-testid={`spotlight-fest-select-${i}`}
                            value={festivals.some((x) => x.date === f.date && (x.name || x.title) === f.label) ? `${f.date}|${f.label}` : ""}
                            onChange={(e) => { const [d, l] = e.target.value.split("|"); setFest(i, { date: d || "", label: l || "" }); }}
                            className={inp}
                          >
                            <option value="">— Choose a festival —</option>
                            {festivals.map((x) => <option key={`${x.id || x.date}-${x.name}`} value={`${x.date}|${x.name || x.title}`}>{(x.name || x.title)} · {x.date}</option>)}
                          </select>
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Date</span>
                          <input type="date" value={f.date || ""} onChange={(e) => setFest(i, { date: e.target.value })} className={inp} data-testid={`spotlight-fest-date-${i}`} />
                        </label>
                      </div>
                      <div className="mt-2 flex items-end gap-2">
                        <div className="flex-1">
                          <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Article</span>
                          <Picker value={f.slug} onChange={(v) => setFest(i, { slug: v })} testid={`spotlight-fest-article-${i}`} />
                        </div>
                        <button onClick={() => removeFest(i)} data-testid={`spotlight-fest-remove-${i}`} className="mb-0.5 inline-flex h-9 w-9 items-center justify-center border border-line text-ink-3 hover:border-[var(--saffron-dark)] hover:text-[var(--saffron-dark)]"><X className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                  {fmap.length === 0 && <p className="text-xs text-ink-3">No festival pins yet.</p>}
                </div>
                <button onClick={addFest} data-testid="spotlight-add-fest" className="mt-3 inline-flex items-center gap-1.5 border border-line px-3 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron"><Plus className="h-4 w-4" /> Add festival pin</button>
              </div>

              {/* Weekly rotation */}
              <div className="border border-line bg-surface p-4" data-testid="spotlight-weekly">
                <div className="mb-1 flex items-center gap-2"><ListOrdered className="h-4 w-4 text-saffron" /><p className="font-serif-latin text-lg text-ink">Weekly rotation</p></div>
                <p className="mb-3 text-xs text-ink-3">A queue that auto-advances one article per day and loops. Used on any day without a festival pin above.</p>
                <div className="space-y-2">
                  {schedule.map((s, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`spotlight-day-row-${i}`}>
                      <span className={`inline-flex h-8 w-16 shrink-0 items-center justify-center border text-xs ${dayIndex() % Math.max(1, schedule.filter(Boolean).length) === i ? "border-saffron bg-[var(--saffron)]/10 text-saffron" : "border-line text-ink-3"}`}>Day {i + 1}</span>
                      <div className="flex-1"><Picker value={s} onChange={(v) => setDay(i, v)} testid={`spotlight-day-article-${i}`} /></div>
                      <button onClick={() => removeDay(i)} data-testid={`spotlight-day-remove-${i}`} className="inline-flex h-9 w-9 shrink-0 items-center justify-center border border-line text-ink-3 hover:border-[var(--saffron-dark)] hover:text-[var(--saffron-dark)]"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {schedule.length === 0 && <p className="text-xs text-ink-3">No rotation days yet.</p>}
                </div>
                {schedule.length < 7 && <button onClick={addDay} data-testid="spotlight-add-day" className="mt-3 inline-flex items-center gap-1.5 border border-line px-3 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron"><Plus className="h-4 w-4" /> Add day ({schedule.length}/7)</button>}
              </div>
            </>
          )}

          {/* Heading copy */}
          <div className="border border-line bg-surface p-4">
            <p className="mb-3 font-serif-latin text-lg text-ink">Section heading</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block"><span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Overline</span><input value={copy.overline || ""} onChange={(e) => setCopyField("overline", e.target.value)} placeholder="Today's Spotlight" className={inp} data-testid="spotlight-overline" /></label>
              <label className="block"><span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Title (English)</span><input value={copy.title_en || ""} onChange={(e) => setCopyField("title_en", e.target.value)} placeholder="In focus today" className={inp} data-testid="spotlight-title-en" /></label>
              <label className="block"><span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Title (Hindi)</span><input value={copy.title_deva || ""} onChange={(e) => setCopyField("title_deva", e.target.value)} placeholder="आज का चयन" className={`${inp} font-deva`} data-testid="spotlight-title-deva" /></label>
              <div className="sm:col-span-2"><span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Description (English)</span><textarea rows={2} value={copy.desc_en || ""} onChange={(e) => setCopyField("desc_en", e.target.value)} placeholder="A fresh reading each day from the living tradition." className={inp} data-testid="spotlight-desc-en" /></div>
              <div className="sm:col-span-2"><span className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Description (Hindi)</span><textarea rows={2} value={copy.desc_deva || ""} onChange={(e) => setCopyField("desc_deva", e.target.value)} placeholder="जीवंत परंपरा से प्रतिदिन एक नया पाठ।" className={`${inp} font-deva`} data-testid="spotlight-desc-deva" /></div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="border border-line bg-surface p-5" data-testid="spotlight-preview">
            <p className="overline-label text-ink-3">Preview · today</p>
            <p className="mt-3 overline-label text-saffron">{copy.overline || "Today's Spotlight"}</p>
            <h3 className="mt-1 font-serif-latin text-2xl text-ink">{copy.title_en || "In focus today"}</h3>
            <p className="mt-2 text-sm text-ink-2">{copy.desc_en || "A fresh reading each day from the living tradition."}</p>
            <div className="mt-4 border-t border-line pt-4">
              {mode === "auto" && <p className="text-sm text-ink-3"><RefreshCw className="mr-1.5 inline h-3.5 w-3.5 text-saffron" /> A katha, stotra or sukta is chosen automatically each day.</p>}
              {mode !== "auto" && (todaysSlug ? (
                <>
                  <p className="overline-label text-gold">{bySlug[todaysSlug]?.type || "article"}</p>
                  <p className="mt-1 font-serif-latin text-lg text-ink">{bySlug[todaysSlug]?.title || todaysSlug}</p>
                  <p className="mt-1 text-xs text-ink-3">{mode === "pinned" ? (pins.filter(Boolean).length > 1 ? `Pinned · rotates among ${pins.filter(Boolean).length} articles` : "Pinned · shown to every visitor") : fmap.some((f) => f.date === todayISO() && f.slug === todaysSlug) ? "Festival pin active today" : "From the weekly rotation"}</p>
                </>
              ) : <p className="text-sm text-ink-3">Nothing scheduled for today yet — the homepage will auto-rotate as a fallback.</p>)}
            </div>
            {mode === "scheduled" && (fmap.length > 0 || schedule.filter(Boolean).length > 0) && (
              <div className="mt-4 space-y-1 border-t border-line pt-3 text-xs text-ink-3">
                {fmap.filter((f) => f.date && f.slug).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 6).map((f, i) => (
                  <div key={i} className="flex justify-between gap-2"><span className="truncate">{f.label || f.date}</span><span className="shrink-0 text-ink-2">{bySlug[f.slug]?.title || f.slug}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
